import React from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import useTracking from '../Tracking/useTracking';
import { mergeDeep, getOptimizedImageUrl } from '../../lib/utils.js';




const StyledSlider = styled(Slider)
`
    .slick-arrow {
        top: 50%;
        z-index: 9;
        width: 0px;
        height: 0px;
        position: absolute;
        border-style: solid;
        transform: translateY(-20px);
        cursor: pointer;
        outline: none;
        font-size: 0;
        line-height: 0;
        color: transparent;
        background: transparent;
    }

    .slick-prev {
        left: 0;
        border-width: 24px 24px 24px 0;
        border-color: ${p => `transparent ${p.arrowBgColor} transparent transparent` };
    }

    .slick-next {
        right: 0;
        border-width: 24px 0 24px 24px;
        border-color: ${p => `transparent transparent transparent ${p.arrowBgColor}` };
    }
`;

const TestimonialWrapper = styled.div `
    position: relative;
    display: flex!important;
    justify-content: space-between;

    @media (max-width: 995px) {
        width: 100%;
        flex-direction: column;
		justify-content: center;
    }
`;

const TestimonialTextWrapper = styled.div `
    width: 46%;
    max-width: 750px;
    min-height: 35vw;
    padding: 78px 0 70px 100px;
    color: ${p => p.textColor};
    text-align: center;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;

    @media (min-width: 1921px) {
        min-height: 650px;
    }

    @media (max-width: 995px) {
        width: 100%;
		justify-content: center;
		text-align: center;
        max-width: none;
		padding: 40px 35px;
		min-height: 0;
		order: 2;
    }

    @media (max-width: 700px) {
        padding: 30px 30px 40px;
    }
`;

const TestimonialTextBlock = styled.div `
    height: 19vw;

    @media (min-width: 1581px) {
        height: 300px;
    }

    @media (max-width: 995px) {
        height: auto;
    }
`;

const TestimonialText = styled.p `
    font-size: 1.5vw;
    line-height: 1.4;
    text-align: left;

    @media (min-width: 1581px) {
        font-size: 24px;
    }

    @media (max-width: 995px) {
        font-size: 115%;
        letter-spacing: 1px;
    }

    @media (max-width: 700px) {
        letter-spacing: 0px;
        font-size: 17px;
    }
`;

const TestimonialAuthorBlock = styled.p `
    font-size: 1.5vw;
    font-weight: 700;
    line-height: 1;
    text-align: right;
    margin-bottom: 40px;
    padding: 0 40px;

    @media (min-width: 1581px) {
        font-size: 24px;
    }

    @media (max-width: 995px) {
        font-size: 115%;
        letter-spacing: 1px;
    }

    @media (max-width: 700px) {
        margin-top: 10px;
        letter-spacing: 0px;
        font-size: 17px;
    }
`;

const TestimonialAuthor = styled.span `
    display: block;
    width: 100%;
`;

const TestimonialAuthorDescription = styled.span `
    display: block;
    width: 100%;
    font-size: 78%;
    font-weight: normal;
`;

const TestimonialCallToAction = styled.a `
    text-decoration: none;
    transition: background .3s;
    padding: 15px 50px;
    color: ${p => p.callToActionColor};
    background: ${p => p.callToActionBgColor};
    text-transform: uppercase;
    font-size: 120%;
    font-weight: 700;
    letter-spacing: 2px;
    border: 0;
    position: relative;
    z-index: 1;
    text-align: center;

    @media (min-width:1331px) {
        font-size: 110%;
    }

    @media (max-width: 995px) {
        width: calc(100% - 50px);
        max-width: 550px;
		z-index: 1;
        margin-bottom: 30px;
        font-size: 3vw;
        padding: 20px 25px;
        letter-spacing: 2px;
    }

    @media (max-width: 700px) {
        font-size: 22px;
        padding: 10px 25px;
        font-weight: 600;
        position: static;
        transform: none;
        display: inline-block;
        margin: 0;
        width: 100%;
    }
`;

const TestimonialPoster = styled.div `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
    width: 100%;
    background-repeat: no-repeat;
    background-position: 100%;
    background-size: cover;
    background-position: 50%;
    //background-image: url(${p => p.bgImage});
    background-image: url(${p => getOptimizedImageUrl(p.bgImage, p.imageQuality)});

    @media (max-width: 995px) {
        order: 0;
        padding-top: 49%;
        position: relative;
        top: auto;
        left: auto;
        right: auto;
        bottom: auto;
        z-index: 1;
        background-repeat: no-repeat;
        background-position: 100% 0;
    }
`;

const TestimonialSlider = ({
    testimonials = [],
    arrowBgColor = '#fff',
    textColor = '#888',
    callToActionColor = '#000',
    callToActionBgColor = '#7e8fbb',
    buttonLink,
    buttonText = 'Start your trial',
    settings,
    imageQuality = '90'
}) => {

    let defaultSettings = {
        infinite: true,
        speed: 2000,
        autoplaySpeed: 5000,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        fade: true,
        adaptiveHeight: true,
        responsive: [{
            breakpoint: 995,
            settings: {
                arrows: false
            }
        }]
    };

    settings = mergeDeep(defaultSettings || {}, settings || {});

    // We don't want more slides then we have items
    if (settings.slidesToShow > testimonials.length) {
        settings.slidesToShow = testimonials.length;
        settings.slidesToScroll = testimonials.length;
    }

    const { trackAndRedirect } = useTracking();

    const handleClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        let payload = { component: 'TestimonialSlider' };
        trackAndRedirect(payload, buttonLink);
    };

    return (
        <StyledSlider {...settings} arrowBgColor={arrowBgColor}>
			{testimonials.map((item,pos) => (
				<TestimonialWrapper key={pos}>
					<TestimonialTextWrapper textColor={textColor}>
						<TestimonialTextBlock>
							<TestimonialText>"{item.testimonial}"</TestimonialText>
							<TestimonialAuthorBlock>
								<TestimonialAuthor>- {item.name}</TestimonialAuthor>
							    <TestimonialAuthorDescription>{item.authorDescription}</TestimonialAuthorDescription>
							</TestimonialAuthorBlock>
						</TestimonialTextBlock>
						<TestimonialCallToAction title="Join Adult Time Now" href="#" callToActionColor={callToActionColor} callToActionBgColor={callToActionBgColor} onClick={handleClick} >
							{buttonText}
						</TestimonialCallToAction>
					</TestimonialTextWrapper>
					<TestimonialPoster bgImage={item.poster} imageQuality={imageQuality} />
				</TestimonialWrapper>
			))}
		</StyledSlider>
    );
};

export default TestimonialSlider;
